// Pagina Ordini (redesign 2026-10, mockup "Ordini B2B goooled").
// Solo l'aspetto è nuovo: funzioni, flussi, permessi e chiamate sono gli stessi della versione precedente.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { OrderContext } from '../context/OrderContext';
import { AuthContext } from '../context/AuthContext';
import '../css/OrdersPage.css';
import { orderToExcel, orderToPdfWithLogo, formatPrice } from "../services/orderService";
import { printProfileLabel, printAllProfileLabels } from "./labelPrint";

import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import {
  FiChevronRight,
  FiFileText,
  FiGrid,
  FiMoreHorizontal,
  FiSend,
  FiEdit2,
  FiCopy,
  FiTrash2,
  FiArchive,
  FiTag,
  FiMail,
  FiPlus,
} from 'react-icons/fi';
import { FaHandshake } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { useItemImage } from './cartImages';
import { getZoneColor, normalizeZone } from './cartZones';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.split(',')[1] : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

// Un ordine è "preventivo per un destinatario" solo se creato con la funzione preventivi
// (Cart.js valorizza recipient solo per utenti quoteEnabled/admin). Per un ordine diretto
// (self-service) non c'è un "cliente terzo" da confermare: resta il flusso storico.
const isQuoteOrder = (order) =>
  !!(order.recipient && (order.recipient.name || order.recipient.company || order.recipient.email));

const eur = (value) => `€ ${formatPrice(value)}`;

const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

// Menu ⋯ della riga: si chiude con un clic fuori o con Esc
const MoreMenu = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span className="op-more" ref={ref}>
      <button
        type="button"
        className="op-iconbtn"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <FiMoreHorizontal aria-hidden="true" />
      </button>
      {open && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="op-pop"
          role="menu"
          // Chiude dopo che la voce ha eseguito la sua azione: chiudendo subito, React
          // smonterebbe il menu prima del clic e l'azione non partirebbe.
          onClickCapture={() => setTimeout(() => setOpen(false), 0)}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </span>
  );
};

// Righe del dettaglio: foto, codice, descrizione, nota, quantità, listino/sconto/netto, totale, etichetta
const OrderLines = ({ order, isAdmin, t }) => {
  const imageOf = useItemImage();
  const items = order.items || [];
  const zoneColorOf = getZoneColor(items);
  const hasZones = items.some((item) => normalizeZone(item.zone));
  let prevZone = null;

  return (
    <div className="op-lines">
      {items.map((item, index) => {
        const zone = normalizeZone(item.zone);
        const color = zone ? zoneColorOf(zone) : null;
        const showBand = hasZones && (index === 0 || zone.toLowerCase() !== prevZone);
        prevZone = zone.toLowerCase();
        const net = item.unitPrice * (1 - item.discount / 100);
        const src = imageOf(item);
        const style = color ? { '--op-zbg': color.bg, '--op-zac': color.accent } : undefined;
        return (
          <React.Fragment key={item._id || index}>
            {showBand && (
              <div className={`op-zone${zone ? '' : ' is-none'}`} style={style}>
                {zone || t('orders.otherItems')}
              </div>
            )}
            <div className={`op-line${zone ? ' has-zone' : ''}`} style={style}>
              <span className="op-line-n">{index + 1}</span>
              {src ? (
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
              ) : (
                <span className="op-noimg" aria-hidden="true" />
              )}
              <span className="op-line-main">
                {item.code && <span className="op-code">{item.code}</span>}
                <span className="op-desc">{item.description}</span>
                {item.notes && <span className="op-note">{t('orders.notes')}: {item.notes}</span>}
              </span>
              <span className="op-q">× {item.quantity}</span>
              <span className="op-p">
                <span>{t('orders.listPrice')} {eur(item.unitPrice)} · −{item.discount}%</span>
                <span>{t('orders.netPrice')} {eur(net)}</span>
              </span>
              <span className="op-t">{eur(item.unitPrice * item.quantity * (1 - item.discount / 100))}</span>
              {isAdmin && (
                <span className="op-lb">
                  {item.code && (
                    <button
                      type="button"
                      className="op-labelbtn"
                      onClick={() => printProfileLabel(item)}
                      title="Stampa etichetta prodotto"
                    >
                      <FiTag aria-hidden="true" /> {t('orders.label')}
                    </button>
                  )}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Orders = ({ searchQuery = '' }) => {
  const { t } = useLanguage();
  const { orders, deleteOrder, sendOrder, confirmCustomerOrder } = useContext(OrderContext);
  const { loadOrderIntoCart, duplicateOrderIntoCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);

  const canDuplicate = !!user && (user.role === 'admin' || !!user.quoteEnabled);
  const isAdmin = user?.role === 'admin';
  const canQuote = isAdmin || !!user?.quoteEnabled;
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('preparato');
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10; // Numero di ordini per pagina
  const navigate = useNavigate();

  // Stato per il popup di conferma
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sending, setSending] = useState(false);

  // Stato per l'ordinamento
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' per ordine discendente (dal più recente)

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1); // Reset paginazione quando cambia filtro
  };

  // Gestisce il cambiamento dell'ordine di ordinamento
  const handleSortOrderChange = (e) => {
    setSortOrder(e.target.value);
    setCurrentPage(1); // Reset paginazione quando cambia l'ordinamento
  };

  // Mostra il popup di conferma
  const handleOpenConfirmDialog = (order, e) => {
    e.stopPropagation();
    setSelectedOrder(order);
    setShowConfirmDialog(true);
  };

  // Chiude il popup di conferma
  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
    setSelectedOrder(null);
  };

  // Esc chiude il popup (non durante l'invio)
  useEffect(() => {
    if (!showConfirmDialog) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !sending) handleCloseConfirmDialog();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showConfirmDialog, sending]);

  // Conferma invio ordine. Se il destinatario ha un'email, genera il PDF del
  // preventivo e lo allega: il backend lo inoltra direttamente al cliente,
  // così l'agente non deve scaricarlo e inviarlo a mano.
  const handleConfirmSendOrder = async () => {
    if (!selectedOrder || sending) return;
    setSending(true);
    try {
      let pdfBase64;
      if (isValidEmail(selectedOrder.recipient?.email)) {
        try {
          const blob = await orderToPdfWithLogo(selectedOrder, user, {
            logoUrl: '/images/logo.png',
            returnBlob: true,
          });
          pdfBase64 = await blobToBase64(blob);
        } catch (pdfErr) {
          console.error('Generazione PDF per invio al cliente fallita:', pdfErr);
        }
      }

      const result = await sendOrder(selectedOrder._id, pdfBase64);

      if (pdfBase64 && result?.customerEmailSent) {
        alert(`${t('orders.sendSuccess')}\nPreventivo inviato via email a ${selectedOrder.recipient.email}.`);
      } else if (pdfBase64 && !result?.customerEmailSent) {
        alert(`${t('orders.sendSuccess')}\nNon è stato possibile inviare il PDF al cliente via email: inviaglielo manualmente.`);
      } else {
        alert(t('orders.sendSuccess'));
      }
    } catch (error) {
      alert(t('orders.sendError'));
    } finally {
      setSending(false);
      handleCloseConfirmDialog();
    }
  };

  const confirmDeleteOrder = (orderId, e) => {
    e.stopPropagation();
    if (window.confirm(t('orders.confirmDelete'))) {
      deleteOrder(orderId);
    }
  };

  const exportOrderToPDF = async (order, e) => {
    e.stopPropagation();
    try {
      await orderToPdfWithLogo(order, user, {
        logoUrl: '/images/logo.png',
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      alert(t('orders.pdfError'));
    }
  };

  const exportOrderToExcel = (order, e) => {
    e.stopPropagation();
    orderToExcel(order);
  };

  const handleEditOrder = (order, e) => {
    e.stopPropagation();
    loadOrderIntoCart(order);
    navigate('/cart');
  };

  const handleDuplicateOrder = (order, e) => {
    e.stopPropagation();
    duplicateOrderIntoCart(order);
    navigate('/cart');
  };

  // Il cliente ha confermato (telefono/mail/di persona): da qui parte la notifica a GOOOLED.
  const handleConfirmCustomer = async (orderId, e) => {
    e.stopPropagation();
    if (!window.confirm('Confermi che il cliente ha accettato questo preventivo? Verrà notificato GOOOLED.')) return;
    try {
      await confirmCustomerOrder(orderId);
    } catch (error) {
      alert("Errore durante la conferma dell'ordine.");
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`Ordine ${orderId} aggiornato a stato: ${newStatus}`);
        resolve();
      }, 500);
    });
  };

  const archiveOrder = async (orderId, e) => {
    e.stopPropagation();
    try {
      await updateOrderStatus(orderId, 'archiviato');
      alert(t('orders.archiveSuccess'));
    } catch (error) {
      alert(t('orders.archiveError'));
    }
  };

  // Filtro degli ordini in base allo stato selezionato e alla ricerca
  const sameCompany = (order) =>
    order.companyKey &&
    user?.companyKey &&
    order.companyKey === user.companyKey &&
    order.companySharingEnabled !== false &&
    user.companySharingEnabled !== false;

  const query = String(searchQuery || '').toLowerCase();
  const filteredOrders = orders
    .filter(order =>
      (
        order.status === filterStatus ||
        (filterStatus === 'inviato' && order.status === 'ricevuto') ||
        (filterStatus === 'preparato' && order.status === 'rifiutato')
      ) &&
      (String(order.userId) === String(user?._id) || sameCompany(order))
    )
    .filter(order =>
      order.orderNumber?.toString().includes(searchQuery) ||  // Cerca nel numero d'ordine
      String(order.reference || '').toLowerCase().includes(query) ||  // Cerca nel riferimento dell'ordine
      (order.items || []).some(item => String(item.description || '').toLowerCase().includes(query))  // Cerca nella descrizione degli articoli
    );

  // Ordina gli ordini filtrati per data
  filteredOrders.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA; // Ordine ascendente o discendente
  });

  // Paginazione degli ordini
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const preparatoCount = orders.filter(order =>
    (order.status === 'preparato' || order.status === 'rifiutato') && order.userId === user._id
  ).length;
  const inviatoCount = orders.filter(order =>
    (order.status === 'inviato' || order.status === 'ricevuto') && order.userId === user._id
  ).length;

  // Stato in parole (stessi casi delle vecchie icone ✓ / ✗ arancio / ✗ rossa)
  const statusOf = (order) => {
    if (order.status === 'ricevuto') return { cls: 'ok', label: t('orders.statusReceived'), title: t('orders.received') };
    if (order.status === 'inviato') {
      if (isQuoteOrder(order) && order.customerConfirmed) {
        return { cls: 'acc', label: t('orders.statusAccepted'), title: 'Cliente confermato, in attesa GOOOLED' };
      }
      return { cls: 'sent', label: t('orders.statusSent'), title: t('orders.sentAwaiting') };
    }
    if (order.status === 'rifiutato') return { cls: 'rej', label: t('orders.statusRejected') };
    if (!order.status || order.status === 'preparato') return { cls: 'prep', label: t('orders.statusPrepared') };
    return { cls: 'prep', label: order.status };
  };

  const renderActions = (order) => {
    const isSent = order.status === 'inviato' || order.status === 'ricevuto';
    const canSendOrEdit = order.status === 'preparato' || order.status === 'rifiutato';
    const canConfirmCustomer = order.status === 'inviato' && isQuoteOrder(order) && !order.customerConfirmed;
    return (
      <span className="op-acts">
        {!isSent && canSendOrEdit && (
          <button type="button" className="op-btn op-btn-small" onClick={(e) => handleOpenConfirmDialog(order, e)} title={t('orders.sendOrder')}>
            <FiSend aria-hidden="true" /> {t('orders.sendShort')}
          </button>
        )}
        {isSent && canConfirmCustomer && (
          <button type="button" className="op-btn op-btn-small" onClick={(e) => handleConfirmCustomer(order._id, e)} title="Il cliente ha confermato: notifica GOOOLED">
            <FaHandshake aria-hidden="true" /> {t('orders.customerAccepted')}
          </button>
        )}
        <button type="button" className="op-iconbtn" onClick={(e) => exportOrderToPDF(order, e)} title={t('orders.exportPdf')} aria-label={t('orders.exportPdf')}>
          <FiFileText aria-hidden="true" />
        </button>
        <button type="button" className="op-iconbtn" onClick={(e) => exportOrderToExcel(order, e)} title={t('orders.exportExcel')} aria-label={t('orders.exportExcel')}>
          <FiGrid aria-hidden="true" />
        </button>
        <MoreMenu label={t('orders.moreActions')}>
          {isSent ? (
            <>
              {isAdmin && (
                <button type="button" onClick={(e) => handleEditOrder(order, e)} title="Modifica (preventivo già inviato: ricordati di rinviarlo)">
                  <FiEdit2 aria-hidden="true" /> {t('orders.editSent')}
                </button>
              )}
              {canDuplicate && (
                <button type="button" onClick={(e) => handleDuplicateOrder(order, e)}>
                  <FiCopy aria-hidden="true" /> {t('orders.duplicateOrder')}
                </button>
              )}
              <button type="button" onClick={(e) => archiveOrder(order._id, e)}>
                <FiArchive aria-hidden="true" /> {t('orders.archive')}
              </button>
            </>
          ) : (
            <>
              {canSendOrEdit && (
                <button type="button" onClick={(e) => handleEditOrder(order, e)}>
                  <FiEdit2 aria-hidden="true" /> {t('orders.editOrder')}
                </button>
              )}
              {canDuplicate && (
                <button type="button" onClick={(e) => handleDuplicateOrder(order, e)}>
                  <FiCopy aria-hidden="true" /> {t('orders.duplicateOrder')}
                </button>
              )}
              <div className="op-pop-sep" />
              <button type="button" className="is-danger" onClick={(e) => confirmDeleteOrder(order._id, e)}>
                <FiTrash2 aria-hidden="true" /> {t('orders.deleteOrder')}…
              </button>
            </>
          )}
        </MoreMenu>
      </span>
    );
  };

  const from = filteredOrders.length ? indexOfFirstOrder + 1 : 0;
  const to = Math.min(indexOfLastOrder, filteredOrders.length);
  const sendEmail = isValidEmail(selectedOrder?.recipient?.email) ? selectedOrder.recipient.email : '';

  return (
    <div className="op">
      <section className="op-head">
        <div>
          <h1>{t('orders.pageTitle')}</h1>
          <p>{t('orders.pageSubtitle')}</p>
        </div>
        <button type="button" className="op-btn is-primary" onClick={() => navigate('/configurator')}>
          <FiPlus aria-hidden="true" /> {canQuote ? t('home.newQuote') : t('home.newOrder')}
        </button>
      </section>

      <section className="op-bar">
        <div className="op-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={filterStatus === 'preparato'}
            onClick={() => handleFilterChange('preparato')}
          >
            {t('orders.toSend')} <span className="op-n">{preparatoCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filterStatus === 'inviato'}
            onClick={() => handleFilterChange('inviato')}
          >
            {t('orders.sent')} <span className="op-n">{inviatoCount}</span>
          </button>
        </div>
        {searchQuery ? (
          <span className="op-searching">{t('orders.searchingFor')} “{searchQuery}”</span>
        ) : null}
        <label className="op-sort">
          <span>{t('orders.sortByDate')}</span>
          <select id="sortOrder" value={sortOrder} onChange={handleSortOrderChange}>
            <option value="desc">{t('orders.recentFirst')}</option>
            <option value="asc">{t('orders.oldestFirst')}</option>
          </select>
        </label>
      </section>

      {currentOrders.length === 0 ? (
        <p className="op-empty">{t('orders.noneFound')}</p>
      ) : (
        <section className="op-list" aria-label={t('orders.title')}>
          <div className="op-cols" aria-hidden="true">
            <span>N°</span>
            <span>{t('orders.colReference')}</span>
            <span className="op-c-by">{t('orders.createdBy')}</span>
            <span className="op-c-date">{t('orders.date')}</span>
            <span>{t('orders.colStatus')}</span>
            <span className="op-r">{t('orders.total')}</span>
            <span className="op-r">{t('orders.colActions')}</span>
          </div>
          {currentOrders.map((order) => {
            const st = statusOf(order);
            const open = expandedOrderId === order._id;
            const creator = order.creatorName || order.creatorEmail || t('orders.unknownUser');
            const recipientName = order.recipient ? (order.recipient.company || order.recipient.name || '-') : '';
            const date = new Date(order.date).toLocaleDateString();
            return (
              <article key={order._id} className={`op-order st-${st.cls}${open ? ' is-open' : ''}`}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div className="op-row" onClick={() => toggleOrderDetails(order._id)}>
                  <span className="op-no op-c-no">
                    <button
                      type="button"
                      className="op-chev"
                      aria-expanded={open}
                      aria-label={`${t('orders.order')} #${order.orderNumber}`}
                      onClick={(e) => { e.stopPropagation(); toggleOrderDetails(order._id); }}
                    >
                      <FiChevronRight aria-hidden="true" />
                    </button>
                    #{order.orderNumber}
                  </span>
                  <span className="op-ref op-c-ref">
                    <strong>{order.reference || '—'}</strong>
                    <span>{order.recipient ? recipientName : t('orders.directOrder')}</span>
                    <span className="op-mini">{date} · {t('orders.createdBy')} {creator}</span>
                    {order.revisedAfterSendAt ? (
                      <span className="op-warnline">⚠ Modificato dopo l'invio il {new Date(order.revisedAfterSendAt).toLocaleDateString()} — da rinviare al cliente</span>
                    ) : null}
                    {order.rejectionReason ? (
                      <span className="op-badline">Motivo rifiuto: {order.rejectionReason}</span>
                    ) : null}
                  </span>
                  <span className="op-by op-c-by">
                    <i aria-hidden="true">{initialsOf(creator)}</i>
                    <span>{creator}</span>
                  </span>
                  <span className="op-date op-c-date">{date}</span>
                  <span className="op-c-pill">
                    <span className={`op-pill p-${st.cls}`} title={st.title}>{st.label}</span>
                  </span>
                  <span className="op-tot op-c-tot">{eur(order.total)}</span>
                  <span className="op-c-acts">{renderActions(order)}</span>
                </div>

                {open && (
                  <div className="op-detail">
                    <div className="op-dhead">
                      <span>{t('orders.recipient')}: <strong>{order.recipient ? recipientName : '—'}</strong></span>
                      <span>{t('orders.createdBy')}: <strong>{creator}</strong></span>
                      <span>{t('orders.date')}: <strong>{date}</strong></span>
                      <span className="op-grow" />
                      {isAdmin && (order.items || []).some(i => i.code) && (
                        <button
                          type="button"
                          className="op-btn op-btn-small"
                          onClick={() => printAllProfileLabels(order.items)}
                        >
                          <FiTag aria-hidden="true" /> Stampa tutte le etichette
                        </button>
                      )}
                    </div>
                    <OrderLines order={order} isAdmin={isAdmin} t={t} />
                    <div className="op-dfoot">
                      <span>{t('orders.total')} <strong>{eur(order.total)}</strong></span>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}

      {/* Paginazione con numeri di pagina */}
      {filteredOrders.length > 0 && (
        <section className="op-pager">
          <span>{t('orders.pagerInfo', { from, to, total: filteredOrders.length, per: ordersPerPage })}</span>
          <div className="op-pages">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                type="button"
                onClick={() => paginate(i + 1)}
                aria-current={currentPage === i + 1 ? 'page' : undefined}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Popup di conferma invio */}
      {showConfirmDialog && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="op-modal" onClick={() => { if (!sending) handleCloseConfirmDialog(); }}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="op-dlg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="confirm-dialog-title">
              {t('orders.confirmSendTitle')}{selectedOrder?.orderNumber ? ` #${selectedOrder.orderNumber}` : ''}
            </h3>
            <p id="confirm-dialog-description">{t('orders.confirmSendText')}</p>
            {sendEmail && (
              <div className="op-mail">
                <FiMail aria-hidden="true" />
                <span>Il PDF del preventivo verrà inviato automaticamente via email a <strong>{sendEmail}</strong>.</span>
              </div>
            )}
            <div className="op-dlg-ft">
              <button type="button" className="op-btn" onClick={handleCloseConfirmDialog} disabled={sending}>
                {t('orders.cancelShort')}
              </button>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <button type="button" className="op-btn is-primary" onClick={handleConfirmSendOrder} disabled={sending} autoFocus>
                <FiSend aria-hidden="true" /> {sending ? t('orders.sending') : t('orders.sendShort')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
